import React from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../../../lib/theme';
import { isPinned } from '../../../../lib/noticePin';
import { fmtDateTime } from './helpers';
import { ActionBtn } from './ActionBtn';
import type { NoticeRow } from './types';

export function NoticeRowCard({ notice, dark, onEdit, onDelete, onTogglePin }: {
  notice: NoticeRow;
  dark: boolean;
  onEdit: () => void;
  onDelete: () => void;
  /** Anpinnen / Pin entfernen — nur übergeben, wenn die Person es darf. */
  onTogglePin?: () => void;
}) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const text = notice.comment ?? notice.nachricht ?? notice.text ?? '—';
  const pinned = isPinned(notice);
  return (
    <div
      data-testid="notice-row"
      data-pinned={pinned ? 'true' : 'false'}
      style={{
        padding: '12px 14px', borderBottom: `1px solid ${t.borderSoft}`, position: 'relative',
        background: pinned ? (dark ? 'rgba(181,41,122,0.12)' : BRAND_LIGHT) : undefined,
        borderLeft: pinned ? `3px solid ${BRAND}` : '3px solid transparent',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, color: t.muted, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {pinned && (
              <span style={{ color: BRAND, fontWeight: 700 }}>📌 {tr('pflege.tasks.notice_pinned')}</span>
            )}
            <span>{fmtDateTime(notice.created_at)}</span>
            {/* Batch-13 Paket E — Personen-Chip: nur im Konto-Modus vorhanden. */}
            {notice.user_name && (
              <span style={{ background: t.hover, border: `1px solid ${t.border}`, padding: '2px 8px', borderRadius: 999 }}>
                {tr('pflege.tasks.person_chip', { name: notice.user_name })}
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: t.text, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{text}</div>
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {onTogglePin && (
            <ActionBtn onClick={onTogglePin} title={tr(pinned ? 'pflege.tasks.notice_unpin' : 'pflege.tasks.notice_pin')} dark={dark}>
              <span aria-hidden style={{ opacity: pinned ? 1 : 0.55 }}>📌</span>
            </ActionBtn>
          )}
          <ActionBtn onClick={onEdit} title={tr('common.action.edit')} dark={dark}>✎</ActionBtn>
          <ActionBtn onClick={onDelete} title={tr('common.action.delete')} dark={dark} danger>✕</ActionBtn>
        </div>
      </div>
    </div>
  );
}
